import { Link, useLocation, useNavigate } from "react-router-dom";
import primaryLogo from "@/assets/cws-logo-primary.webp";
import squareLogo from "@/assets/cws-logo.webp";
import lightLogo from "@/assets/cws-logo-light.png";

export const CWS_LIGHT_LOGO_URL = lightLogo;

type Props = {
  className?: string;
  variant?: "default" | "light";
  size?: "sm" | "md" | "lg" | "xl";
  /** "primary" = full horizontal lockup (header), "square" = mark only (footer/mobile) */
  kind?: "primary" | "square";
  /** Optional override for the link/navigation target. Defaults to "/" */
  to?: string;
  /** Optional override logo src. Defaults to the brand primary/square lockups. */
  src?: string;
};

/**
 * Official California Waste Solutions logo.
 * Aspect ratios are preserved via explicit width/height attributes + w-auto.
 *  - primary lockup: 1920 x 574  (~3.34:1)
 *  - square mark:    1920 x 1543 (~1.24:1)
 */
export const Logo = ({
  className = "",
  variant = "default",
  size = "md",
  kind = "primary",
  to = "/",
  src,
}: Props) => {
  const isPrimary = kind === "primary";
  const logoSrc = src || (isPrimary ? primaryLogo : squareLogo);

  // Heights only — width is auto so the image is never squeezed.
  const heightCls = isPrimary
    ? size === "sm"
      ? "h-11"
      : size === "xl"
      ? "h-16 md:h-[72px] lg:h-20"
      : size === "lg"
      ? "h-11 md:h-12 xl:h-[52px] 2xl:h-[60px]"
      : "h-10 md:h-11 xl:h-12 2xl:h-[54px]"
    : size === "sm"
    ? "h-12"
    : size === "xl"
    ? "h-24 md:h-28"
    : size === "lg"
    ? "h-20 md:h-24"
    : "h-16 md:h-20";

  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (e: React.MouseEvent) => {
    if (location.pathname === to) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      e.preventDefault();
      navigate(to);
      window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    }
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      aria-label="California Waste Solutions: Home"
      className={`inline-flex shrink-0 items-center ${className}`}
    >
      <img loading="lazy" decoding="async"
        src={logoSrc}
        alt="California Waste Solutions"
        width={isPrimary ? 1920 : 1920}
        height={isPrimary ? 574 : 1543}
        className={`${heightCls} w-auto max-w-none select-none object-contain ${
          variant === "light" ? "drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]" : ""
        }`}
        draggable={false}
      />
    </Link>
  );
};
